import type { ExtractedEvents } from '../av/events';
import type { AudioBand, EventCategory } from '../domain/schemas';
import { BAND_COUNT, SERIES, type AudioFeatures } from './analysis';

/**
 * Stateless evaluation of audio-reactive inputs at any session position (Phase 4C).
 * Nothing here keeps state between frames, so seeking to t gives exactly what playing to t
 * gives, and preview and export agree.
 *
 *  - Measured: a series value at t (linear interpolation between 60 Hz analysis frames),
 *    smoothed by a moving average over the last ⌈smoothing·10⌉ frames (prefix sums, O(1)),
 *    then held with an exponential release:  max_j v(t − j·hop) · e^(−j·hop/decay)  over a
 *    bounded look-back (⌈3·decay/hop⌉ frames, at most 180).
 *  - Events: the strongest decaying envelope of matching musical events before t
 *    (velocity · e^(−age/decay)), found by binary search; bounded look-back.
 */

export const MAX_SMOOTH_FRAMES = 10;
export const MAX_DECAY_FRAMES = 180;

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Mean of series `s` over frames [k − w, k] (clamped to the data). */
function windowMean(f: AudioFeatures, s: number, k: number, w: number): number {
  const p = f.prefix[s]!;
  const hi = Math.min(f.frames, Math.max(0, k + 1));
  const lo = Math.max(0, Math.min(hi, k - w));
  return hi > lo ? (p[hi]! - p[lo]!) / (hi - lo) : 0;
}

/** Smoothed value of series `s` at time t (interpolated between frames). */
export function smoothedAt(f: AudioFeatures, s: number, t: number, smoothing: number): number {
  const x = t / f.hopSec;
  if (x < 0 || x > f.frames) return 0;
  const k = Math.floor(x);
  const frac = x - k;
  const w = Math.round(clamp01(smoothing) * MAX_SMOOTH_FRAMES);
  const a = windowMean(f, s, k, w);
  if (frac === 0) return a;
  return a + (windowMean(f, s, k + 1, w) - a) * frac;
}

/** Smoothed, decayed value of series `s` at t (0–1, before sensitivity). */
export function levelAt(f: AudioFeatures, s: number, t: number, smoothing: number, decay: number): number {
  let v = smoothedAt(f, s, t, smoothing);
  if (decay <= 0) return v;
  const frames = Math.min(MAX_DECAY_FRAMES, Math.ceil((3 * decay) / f.hopSec));
  for (let j = 1; j <= frames; j++) {
    const held = smoothedAt(f, s, t - j * f.hopSec, smoothing) * Math.exp((-j * f.hopSec) / decay);
    if (held > v) v = held;
  }
  return v;
}

export const bandSeries = (band: AudioBand): number => SERIES[band];

/** All 32 band levels at t (decayed and smoothed), for spectrum layers. */
export function spectrumAt(f: AudioFeatures, t: number, smoothing: number, decay: number, out = new Float32Array(BAND_COUNT)): Float32Array {
  for (let b = 0; b < BAND_COUNT; b++) out[b] = levelAt(f, b, t, smoothing, decay);
  return out;
}

/** Waveform sample (−1…1) at time t (nearest 4 kHz sample; 0 outside the analysed range). */
export function waveAt(f: AudioFeatures, t: number): number {
  const i = Math.round(t * f.waveRate);
  return i >= 0 && i < f.waveform.length ? f.waveform[i]! / 32767 : 0;
}

// ------------------------------------------------------------------ events

export interface EventTrack {
  /** Event times (session seconds), ascending. */
  times: Float64Array;
  velocities: Float32Array;
  /** Stable index of each event in the extraction (for seeded per-event randomness). */
  ordinals: Int32Array;
}

const trackCache = new WeakMap<ExtractedEvents, Map<string, EventTrack>>();

/** Times and velocities of events in `categories` (cached per extraction). */
export function eventTrack(events: ExtractedEvents, categories: readonly EventCategory[]): EventTrack {
  let byKey = trackCache.get(events);
  if (!byKey) trackCache.set(events, (byKey = new Map()));
  const key = [...categories].sort().join(',');
  let track = byKey.get(key);
  if (!track) {
    const set = new Set(categories);
    const matching: { t: number; v: number; o: number }[] = [];
    events.events.forEach((e, i) => {
      if (set.has(e.category)) matching.push({ t: e.seconds, v: e.velocity, o: i });
    });
    matching.sort((a, b) => a.t - b.t || a.o - b.o);
    track = { times: Float64Array.from(matching, (m) => m.t), velocities: Float32Array.from(matching, (m) => m.v), ordinals: Int32Array.from(matching, (m) => m.o) };
    byKey.set(key, track);
  }
  return track;
}

/** Index of the last event at or before t (−1 when none). */
export function lastAtOrBefore(times: Float64Array, t: number): number {
  let lo = 0;
  let hi = times.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid]! <= t) lo = mid + 1;
    else hi = mid;
  }
  return lo - 1;
}

/** Envelope level of an event track at t: the strongest velocity · e^(−age/decay) (0–1). */
export function eventLevelAt(track: EventTrack, t: number, decay: number): number {
  const d = Math.max(0.02, decay);
  let v = 0;
  for (let i = lastAtOrBefore(track.times, t), n = 0; i >= 0 && n < 64; i--, n++) {
    const age = t - track.times[i]!;
    if (age > 4 * d) break;
    const level = (0.35 + 0.65 * track.velocities[i]!) * Math.exp(-age / d);
    if (level > v) v = level;
  }
  return v;
}

/** Deterministic pseudo-random in [0, 1) from integers (no state). */
export function hash01(a: number, b: number, c = 0, d = 0): number {
  let h = Math.imul(a | 0, 0x9e3779b1) ^ Math.imul(b | 0, 0x85ebca77) ^ Math.imul(c | 0, 0xc2b2ae3d) ^ Math.imul(d | 0, 0x27d4eb2f);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}
